import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Quote, Trash2, X } from "lucide-react";
import { api } from "../../api";
import { useApp } from "../../store";

const inputCls =
  "w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300";

const EMPTY = { quote_fr: "", quote_en: "", author: "", role_fr: "", role_en: "" };

export const TestimonialsManager = () => {
  const { t } = useApp();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/testimonials").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      if (editingId) await api.put(`/admin/testimonials/${editingId}`, form);
      else await api.post("/admin/testimonials", form);
      toast.success(t("admin_saved"));
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      quote_fr: item.quote_fr,
      quote_en: item.quote_en,
      author: item.author,
      role_fr: item.role_fr,
      role_en: item.role_en,
    });
  };

  const remove = async (item) => {
    await api.delete(`/admin/testimonials/${item.id}`);
    load();
  };

  const canSave = form.quote_fr.trim().length >= 2 && form.author.trim().length >= 1;

  return (
    <div data-testid="admin-testimonials-panel" className="flex flex-col gap-8">
      <div className="border border-white/10 bg-white/[0.02] p-6">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
          {editingId ? t("admin_editing") : t("admin_testimonial_add")}
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <textarea
            data-testid="admin-testimonial-quote-fr"
            rows={3}
            value={form.quote_fr}
            onChange={set("quote_fr")}
            placeholder={t("admin_testimonial_quote_fr")}
            maxLength={1000}
            className={`${inputCls} resize-y`}
          />
          <textarea
            data-testid="admin-testimonial-quote-en"
            rows={3}
            value={form.quote_en}
            onChange={set("quote_en")}
            placeholder={t("admin_testimonial_quote_en")}
            maxLength={1000}
            className={`${inputCls} resize-y`}
          />
          <input
            data-testid="admin-testimonial-author"
            value={form.author}
            onChange={set("author")}
            placeholder={t("admin_testimonial_author")}
            maxLength={120}
            className={inputCls}
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              data-testid="admin-testimonial-role-fr"
              value={form.role_fr}
              onChange={set("role_fr")}
              placeholder={t("admin_testimonial_role_fr")}
              maxLength={150}
              className={inputCls}
            />
            <input
              data-testid="admin-testimonial-role-en"
              value={form.role_en}
              onChange={set("role_en")}
              placeholder={t("admin_testimonial_role_en")}
              maxLength={150}
              className={inputCls}
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            data-testid="admin-testimonial-save"
            onClick={save}
            disabled={busy || !canSave}
            className="inline-flex items-center gap-2 text-xs bg-[var(--accent)] hover:bg-[var(--accent-soft)] disabled:opacity-60 text-white font-semibold px-5 py-3 rounded-full transition-colors duration-300"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {busy ? t("admin_saving") : t("admin_save")}
          </button>
          {editingId && (
            <button
              data-testid="admin-testimonial-cancel"
              onClick={() => {
                setEditingId(null);
                setForm(EMPTY);
              }}
              className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-white/40 px-4 py-3 rounded-full transition-colors duration-300"
            >
              <X size={13} />
              {t("admin_cancel")}
            </button>
          )}
        </div>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-[var(--accent)]" size={26} />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="admin-testimonials-empty" className="text-white/45 py-12 text-center font-display italic text-xl">
          {t("admin_testimonials_empty")}
        </p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((item) => (
            <article key={item.id} data-testid={`admin-testimonial-${item.id}`} className="border border-white/10 bg-white/[0.02] p-6">
              <Quote size={18} className="text-[var(--accent)] mb-3" />
              <p className="font-display italic text-lg leading-relaxed text-white/85">« {item.quote_fr} »</p>
              <p className="mt-4 text-sm font-semibold">{item.author}</p>
              {item.role_fr && (
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[var(--accent)] mt-1">{item.role_fr}</p>
              )}
              <div className="mt-5 flex gap-2">
                <button
                  data-testid={`admin-testimonial-edit-${item.id}`}
                  onClick={() => startEdit(item)}
                  className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-white/40 px-3 py-2 rounded-full transition-colors duration-300"
                >
                  <Pencil size={13} />
                  {t("admin_edit")}
                </button>
                <button
                  data-testid={`admin-testimonial-delete-${item.id}`}
                  onClick={() => remove(item)}
                  className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-[var(--accent)] hover:text-[var(--accent)] px-3 py-2 rounded-full transition-colors duration-300"
                >
                  <Trash2 size={13} />
                  {t("admin_delete")}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
